import { useState, useEffect } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { useMonthlyRevenue } from "@/hooks/use-transactions";
import { useExecutiveRevenueData } from "@/hooks/use-executive-demo-data";
import { formatSwissCurrency } from "@/integrations/supabase/client";
import { formatDemoCurrency } from "@/lib/demo-data";
import { RealtimeStatusIndicator } from "@/components/realtime/realtime-status-indicator";
import { DashboardErrorFallback, DashboardLoadingFallback } from "@/components/error/dashboard-error-fallback";

interface RevenueChartProps {
  demoMode?: boolean;
}

export const RevenueChart: React.FC<RevenueChartProps> = ({ demoMode = true }) => {
  // Use demo data for Executive View, real data for Operational View
  const demoDataHook = useExecutiveRevenueData();
  const realDataHook = useMonthlyRevenue(12);
  
  const { data: revenueData, isLoading, refetch, error } = demoMode ? demoDataHook : realDataHook;
  const formatCurrency = demoMode ? formatDemoCurrency : formatSwissCurrency;

  // Use real data if available, otherwise show empty state
  const chartData = revenueData && revenueData.length > 0 ? revenueData : [];

  // Show error state
  if (error) {
    return (
      <DashboardErrorFallback
        title="Revenue Data Unavailable"
        description={error.message || "Unable to load revenue forecasting data"}
        onRetry={refetch}
        variant="default"
      />
    );
  }

  // Show loading state
  if (isLoading && !revenueData) {
    return (
      <DashboardLoadingFallback
        title="Loading revenue data..."
        height="h-[320px]"
      />
    );
  }

  // Show empty state when no data is available
  if (chartData.length === 0) {
    return (
      <div className="bg-card p-6 rounded-2xl border">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-bold text-brand-primary">Revenue Forecasting</h3>
            <RealtimeStatusIndicator variant="icon" size="sm" />
          </div>
          <button
            onClick={() => refetch()}
            className="btn-swiss-secondary h-7 px-2 py-1.5 rounded-[7px] text-[10px] leading-[10px] hover:bg-brand-secondary transition-colors"
            title="Refresh data"
          >
            <i className="fa-solid fa-refresh mr-1" />
            Refresh
          </button>
        </div>
        <div className="h-[280px] flex items-center justify-center text-center">
          <div className="text-muted-foreground">
            <i className="fa-solid fa-chart-area fa-2x mb-4" />
            <p className="text-lg font-medium mb-2">No Revenue Data</p>
            <p className="text-sm">
              Revenue data will appear here once you upload and process your first transactions.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const totalActual = chartData.reduce((sum, item) => sum + (item.actual || 0), 0);
  const totalForecast = chartData.reduce((sum, item) => sum + (item.forecast || 0), 0);
  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-brand-primary">Revenue Forecasting</h3>
          <RealtimeStatusIndicator variant="icon" size="sm" />
          {isLoading && (
            <Badge variant="secondary" className="text-xs animate-pulse">
              Updating...
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-brand-primary" />
            <span className="text-xs">Actual ({formatCurrency(totalActual, true)})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-brand-accent" />
            <span className="text-xs">Forecast ({formatCurrency(totalForecast, true)})</span>
          </div>
          <button
            onClick={() => refetch()}
            className="btn-swiss-secondary h-7 px-2 py-1.5 rounded-[7px] text-[10px] leading-[10px] hover:bg-brand-secondary transition-colors"
            title="Refresh data"
          >
            <i className="fa-solid fa-refresh mr-1" />
            Refresh
          </button>
        </div>
      </div>

      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--brand-primary))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--brand-primary))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--brand-accent))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--brand-accent))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `CHF ${value / 1000}k`}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                `CHF ${value?.toLocaleString()}`,
                name === "actual" ? "Actual Revenue" : "Forecasted Revenue"
              ]}
              labelStyle={{ color: "hsl(var(--foreground))" }}
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
            />
            <Area
              type="monotone"
              dataKey="actual"
              stroke="hsl(var(--brand-primary))"
              fill="url(#actualGradient)"
              strokeWidth={2}
              connectNulls={false}
            />
            <Area
              type="monotone"
              dataKey="forecast"
              stroke="hsl(var(--brand-accent))"
              fill="url(#forecastGradient)"
              strokeWidth={2}
              strokeDasharray="5 5"
              connectNulls={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};